import { customElement, scss } from '../../core/ids-decorators';
import { attributes, htmlAttributes } from '../../core/ids-attributes';
import IdsEventsMixin from '../../mixins/ids-events-mixin/ids-events-mixin';
import IdsTooltipMixin from '../../mixins/ids-tooltip-mixin/ids-tooltip-mixin';
import IdsElement from '../../core/ids-element';

import styles from './ids-list-box-option.scss';
import { stringToBool } from '../../utils/ids-string-utils/ids-string-utils';

import '../ids-checkbox/ids-checkbox';
import '../ids-icon/ids-icon';

import type IdsCheckbox from '../ids-checkbox/ids-checkbox';
import type IdsIcon from '../ids-icon/ids-icon';
import IdsTooltip from '../ids-tooltip/ids-tooltip';

const Base = IdsTooltipMixin(
  IdsEventsMixin(
    IdsElement
  )
);

/**
 * IDS List Box Option Component
 * @type {IdsListBoxOption}
 * @inherits IdsElement
 * @mixes IdsEventsMixin
 * @mixes IdsTooltipMixin
 * @part option - the option element
 */
@customElement('ids-list-box-option')
@scss(styles)
export default class IdsListBoxOption extends Base {
  #classChangeObserver: MutationObserver | null = null;

  #slotMutationObserver: MutationObserver | null = null;

  constructor() {
    super();
  }

  /**
   * Return the attributes we handle as getters/setters
   * @returns {Array<any>} The attributes in an array
   */
  static get attributes(): Array<any> {
    return [
      ...super.attributes,
      attributes.DISABLED,
      attributes.GROUP_LABEL,
      attributes.ROW_INDEX,
      attributes.SELECTED,
      attributes.TOOLTIP,
      attributes.VALUE
    ];
  }

  connectedCallback() {
    super.connectedCallback();
    this.setAttribute(htmlAttributes.ROLE, this.groupLabel ? 'none' : 'option');
    this.setAttribute('tabindex', '-1');
    this.#configureCheckbox();
    this.#hideEmptyGroupOption();
    this.#attachEventHandlers();
    this.#observeClassChanges();
  }

  /**
   * Cleanup observers and event listeners when element is removed from DOM
   * @returns {void}
   */
  disconnectedCallback() {
    super.disconnectedCallback?.();
    this.#classChangeObserver?.disconnect();
    this.#slotMutationObserver?.disconnect();
  }

  /**
   * Establish Internal Event Handlers
   * @returns {void}
   */
  #attachEventHandlers(): void {
    const slot = this.shadowRoot?.querySelector('slot');
    const listBox = this.parentElement;

    this.offEvent('slotchange.list-box-option', slot);
    this.onEvent('slotchange.list-box-option', slot, () => {
      const assignedNode = slot?.assignedNodes()?.[0];
      const mutationObserver = (mutationList: any[]) => {
        for (const m of mutationList) {
          if (m.target === this || m.target === assignedNode) {
            listBox?.dispatchEvent(new CustomEvent('slotchange', { bubbles: true }));
          }
        }
      };

      this.#slotMutationObserver = new MutationObserver(mutationObserver);
      this.#slotMutationObserver.observe(this, {
        childList: true,
        subtree: true,
        characterData: true
      });
    });
  }

  /**
   * Observe class changes to reconfigure checkbox visibility
   * @returns {void}
   */
  #observeClassChanges(): void {
    this.#classChangeObserver = new MutationObserver(() => {
      this.#configureCheckbox();
    });

    this.#classChangeObserver.observe(this, {
      attributes: true,
      attributeFilter: ['class']
    });
  }

  #hideEmptyGroupOption() {
    const nextOption = this.nextElementSibling as IdsListBoxOption;
    this.hidden = this.groupLabel && (!nextOption || nextOption.groupLabel);
  }

  #configureCheckbox() {
    if (this.parentElement?.tagName === 'IDS-LIST-BOX') {
      const parentEl = this.parentElement.parentElement;
      const checkBox = this?.childCheckbox;
      const showCheckboxes = this.classList.contains('show-checkboxes');

      if (checkBox && !this.groupLabel) {
        if (showCheckboxes) {
          // Show checkbox when show-checkboxes is enabled
          checkBox.input?.removeAttribute(attributes.HIDDEN);
          checkBox.input?.removeAttribute(htmlAttributes.ROLE);
        } else {
          // Hide checkbox by default
          checkBox.input?.setAttribute(attributes.HIDDEN, '');
          checkBox.input?.setAttribute(htmlAttributes.ROLE, 'presentation');
        }
      }

      const checkBoxRole = this.getAttribute(htmlAttributes.ROLE);
      if (checkBoxRole) {
        checkBox?.setAttribute(htmlAttributes.ROLE, checkBoxRole);
        checkBox?.input?.setAttribute(htmlAttributes.ROLE, checkBoxRole);
      } else {
        checkBox?.removeAttribute(htmlAttributes.ROLE);
        checkBox?.input?.removeAttribute(htmlAttributes.ROLE);
      }

      if (parentEl?.hasAttribute(attributes.OVERFLOW)) {
        checkBox?.classList.add('ellipsis');
      }
    }
  }

  /**
   * Create the template for the contents
   * @returns {string} The template
   */
  template(): string {
    return `<slot></slot><slot name="info"></slot>`;
  }

  /**
   * Get nested ids-checkbox child
   * @returns {IdsCheckbox | undefined} - nested ids-checkbox child
   */
  get childCheckbox(): IdsCheckbox | undefined {
    return this?.querySelector<IdsCheckbox>('ids-checkbox') ?? undefined;
  }

  get childIcon(): IdsIcon | undefined {
    return this.querySelector<IdsIcon>('ids-icon') ?? undefined;
  }

  get label(): string { return this.textContent?.trim() || this.childCheckbox?.label || ''; }

  get groupLabel(): boolean { return this.hasAttribute(attributes.GROUP_LABEL); }

  set groupLabel(value) {
    if (stringToBool(value)) {
      this.setAttribute(attributes.GROUP_LABEL, 'true');
    } else {
      this.removeAttribute(attributes.GROUP_LABEL);
    }
  }

  set value(value: string | null | undefined) {
    this.setAttribute(attributes.VALUE, `${value ?? ''}`);
    // This case happens in NG see IDS-6198
    if (this.getAttribute(attributes.VALUE) === 'undefined') {
      this.setAttribute(attributes.VALUE, this.id);
    }

    for (let j = 0; j < this.children.length; j++) {
      if (this.children[j].classList.contains('multi-key')) {
        const multiKeyEl = this.children[j];
        for (let i = 0; i < multiKeyEl.children.length; i++) {
          const childEl: HTMLElement | any = multiKeyEl.children[i];
          if (childEl.classList.contains('highlight')) {
            childEl.style.fontWeight = 700;
          }
        }
      }
    }
  }

  get value(): string | null | undefined { return this.getAttribute(attributes.VALUE) ?? ''; }

  /**
   * Set the selected state on the list-box-option
   * @param {boolean} val true if this option should appear "selected"
   */
  set selected(val) {
    // Prevents NG from firing a change event in the Form Accessors
    this.childCheckbox?.onEvent('change', this.childCheckbox, (e: Event) => {
      e.stopPropagation();
      e.stopImmediatePropagation();
    });

    if (stringToBool(val)) {
      this.setAttribute(attributes.SELECTED, 'true');
      this.container?.classList.add('is-selected');
      if (this.childCheckbox) this.childCheckbox.checked = true;
    } else {
      this.removeAttribute(attributes.SELECTED);
      this.container?.classList.remove('is-selected');
      if (this.childCheckbox) this.childCheckbox.checked = false;
    }
  }

  get selected() {
    return stringToBool(this.getAttribute(attributes.SELECTED));
  }

  /**
   * Set the row index. This index will be used to sort options in ids-list-box component.
   * @param {number} value the index
   */
  set rowIndex(value: number) {
    if (value !== null && value >= 0) {
      this.setAttribute(attributes.ROW_INDEX, String(value));
    } else {
      this.removeAttribute(attributes.ROW_INDEX);
    }
  }

  /**
   * Gets the row index # of this row.
   * @returns {number} the row-index
   */
  get rowIndex(): number { return Number(this.getAttribute(attributes.ROW_INDEX) ?? -1); }

  /**
   * Sets the disabled attribute
   * @param {boolean | string} value string value from the disabled attribute
   */
  set disabled(value: boolean | string) {
    if (stringToBool(value)) {
      this.setAttribute('aria-disabled', 'true');
      this.setAttribute(attributes.DISABLED, 'true');
    } else {
      this.removeAttribute('aria-disabled');
      this.removeAttribute(attributes.DISABLED);
    }
  }

  /**
   * Get the disabled on the list-box-option
   * @returns {boolean | string} True if disabled, otherwise false
   */
  get disabled(): boolean | string {
    return this.hasAttribute(attributes.DISABLED);
  }

  /*
   * Determines whether a tooltip should be shown for the list box option.
   */
  canTooltipShow(_tooltipEl: IdsTooltip | null, tooltipContent: string): boolean {
    return (this.tooltip === 'true' && this.scrollWidth > this.clientWidth) || (this.tooltip !== 'true' && tooltipContent.length > 0);
  }

  /**
   * Returns plain label text for overflow tooltips instead of raw innerHTML.
   * @returns {string} The tooltip content
   */
  getTooltipContent(): string {
    return this.tooltip === 'true' ? (this.childCheckbox?.label || this.textContent?.trim() || '') : this.tooltip;
  }
}
